import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useOrgFilter } from "./OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { getAccessToken } from "@/lib/authSession";

const SocialDmSocketContext = createContext(null);

/**
 * Socket.IO for social-plane DMs. Connects when Personal/social is selected;
 * disconnects when a workplace org is active.
 */
export function SocialDmSocketProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);

  const isSocial =
    !selectedOrgId || selectedOrgId === "social" || selectedOrgId === "";

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const emitLocal = useCallback((event, payload) => {
    for (const handler of listenersRef.current) {
      try {
        handler(event, payload);
      } catch (err) {
        console.error("Social DM socket listener error:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isSocial) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      return undefined;
    }

    const socket = io(`${API_BASE_URL}/social-dm`, {
      auth: (cb) => cb({ token: getAccessToken() }),
      transports: ["websocket", "polling"],
      withCredentials: true,
    });
    socketRef.current = socket;

    const onConnect = () => {
      setConnected(true);
      socket.emit("social-dm.join", {});
    };
    const onDisconnect = () => setConnected(false);
    const onMessage = (payload) => emitLocal("social-dm.message", payload);
    const onRead = (payload) => emitLocal("social-dm.read", payload);
    const onTyping = (payload) => emitLocal("social-dm.typing", payload);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("social-dm.message", onMessage);
    socket.on("social-dm.read", onRead);
    socket.on("social-dm.typing", onTyping);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("social-dm.message", onMessage);
      socket.off("social-dm.read", onRead);
      socket.off("social-dm.typing", onTyping);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
      setConnected(false);
    };
  }, [isAuthenticated, isSocial, emitLocal]);

  const emitTyping = useCallback((conversationId, typing) => {
    const socket = socketRef.current;
    if (!socket || conversationId == null) return;
    socket.emit("social-dm.typing", {
      conversation_id: Number(conversationId),
      typing: !!typing,
    });
  }, []);

  const value = {
    connected,
    isSocial,
    subscribe,
    emitTyping,
  };

  return (
    <SocialDmSocketContext.Provider value={value}>
      {children}
    </SocialDmSocketContext.Provider>
  );
}

export function useSocialDmSocket() {
  const ctx = useContext(SocialDmSocketContext);
  if (!ctx) {
    throw new Error(
      "useSocialDmSocket must be used within SocialDmSocketProvider"
    );
  }
  return ctx;
}

export function useOptionalSocialDmSocket() {
  return useContext(SocialDmSocketContext);
}
